import React, { useEffect, useState } from 'react';

import { makeStyles } from '@material-ui/core/styles';
import Autocomplete from '@material-ui/lab/Autocomplete';

import cls from './CountriesInput.module.css';
import s from '../Register/Step/StepFour/StepFour.module.css';
import { TextField } from '../TextField';

const useStyles = makeStyles((theme) => ({
    root: {
        '& .MuiFilledInput-root': {
            backgroundColor: '#F7F7F7',
            padding: '0',
        },
        '& .MuiFilledInput-root .Mui-error': {
            backgroundColor: '#F7F7F7',
        },
        '& .MuiFormLabel-root': {
            color: '#8692A6',
        },
        marginBottom: '0 !important',
        error: {
            border: '1px solid #F90000',
        },
    },

    paper: {
        width: '100%',
        marginLeft: -1,
        boxShadow: '0px 5px 20px rgba(31, 50, 61, 0.19) !important',
    },
    option: {
        '&:hover': {
            backgroundColor: '#F2F6F7',
            marginBottom: 0,
        },
        '&[aria-selected="true"]': {
            color: '#1094F7',
            borderLeft: '2px solid #1094F7',
            backgroundColor: 'transparent',
        },
        [theme.breakpoints.down('xs')]: {
            fontSize: 14,
            minHeight: 35,
            padding: '10px 12px',
            lineHeight: '15.4px',
        },
    },
}));

const RenderInput = (props) => (
    <TextField label={i18next.t('CountriesInput.return.RenderInput.label')} {...props} />
);

const LISTBOX_ID = 'country-options-listbox';

export const CountriesInput = ({ updateCountry, countryList, setErrors, errors }) => {
    const styles = useStyles();
    const [selectedCountry, setSelectedCountry] = useState(null);
    const [inputValue, setInputValue] = useState('');

    const options = countryList || [];

    const writeError = (e) => {
        const inputText = e.target.value.trim();
        const isValid = !!selectedCountry;

        if (!inputText || !isValid) {
            setErrors((prevState) => ({
                ...prevState,
                country: i18next.t('CityInput.return.writeError.cityField'),
            }));
        } else {
            setErrors((prevState) => ({ ...prevState, country: null }));
        }
    };

    useEffect(() => {
        const defaultCountry = options?.find((country) => country.isDefault);
        if (defaultCountry) {
            setSelectedCountry(defaultCountry);
            setInputValue(defaultCountry.name);
            updateCountry(defaultCountry.id);
        }
    }, [options, updateCountry]);

    const handleOpen = () => {
        requestAnimationFrame(() => {
            const listbox = document.querySelector(`#${LISTBOX_ID}`);
            if (!listbox) return;

            const selectedItem = listbox.querySelector('[aria-selected="true"]');
            if (!selectedItem) return;

            const listboxEl = listbox as HTMLElement;
            const itemEl = selectedItem as HTMLElement;

            const listboxHeight = listboxEl.clientHeight;
            const itemOffsetTop = itemEl.offsetTop;
            const itemHeight = itemEl.offsetHeight;

            const scrollTop = itemOffsetTop - (listboxHeight - itemHeight) / 2;

            listboxEl.scrollTop = scrollTop;
        });
    };

    return (
        <div className={s.autocompleteWrapper}>
            <Autocomplete
                classes={styles}
                popupIcon={false}
                options={options}
                openOnFocus
                onOpen={handleOpen}
                ListboxProps={{
                    id: LISTBOX_ID,
                    'data-id': 'country-options-list',
                }}
                data-id="country-input"
                noOptionsText={i18next.t('CityInput.return.Autocomplete.noOptionsText')}
                value={selectedCountry}
                inputValue={inputValue}
                getOptionLabel={(option) => `${option.name}`}
                getOptionSelected={(option, value) => option.id === value.id}
                onChange={(e, value, reason) => {
                    const nextValue = value || null;
                    setSelectedCountry(nextValue);

                    if (reason === 'select-option') {
                        setErrors((prevState) => ({ ...prevState, country: null }));
                    }

                    if (reason === 'clear' || !nextValue) {
                        updateCountry('');
                        setInputValue('');
                    } else {
                        updateCountry(nextValue.id);
                        setInputValue(nextValue.name);
                    }
                }}
                onInputChange={(e, newInputValue, reason) => {
                    if (reason === 'input') {
                        setInputValue(newInputValue);

                        if (selectedCountry) {
                            setSelectedCountry(null);
                            updateCountry('');
                        }
                    }

                    if (reason === 'clear') {
                        setInputValue('');
                        setSelectedCountry(null);
                        updateCountry(null);
                    }
                }}
                renderInput={(params) => (
                    <RenderInput
                        {...params}
                        inputProps={{
                            ...params.inputProps,
                            onBlur: (event) => {
                                if (params.inputProps.onBlur) {
                                    params.inputProps.onBlur(event);
                                }
                                writeError(event);
                            },
                            onKeyDown: (event) => {
                                if (event.key === 'Enter') {
                                    event.preventDefault();
                                }

                                if (params.inputProps.onKeyDown) {
                                    params.inputProps.onKeyDown(event);
                                }
                            },
                        }}
                        InputProps={{
                            ...params.InputProps,
                            startAdornment: selectedCountry?.flagPath ? (
                                <span className={cls.countryFlagSelected}>
                                    <img
                                        src={selectedCountry.flagPath}
                                        alt="country-flag-icon"
                                        width={25}
                                        height={15}
                                    />
                                    {params.InputProps.startAdornment}
                                </span>
                            ) : (
                                params.InputProps.startAdornment
                            ),
                        }}
                        error={!!errors.country}
                        helperText={errors.country ?? ' '}
                    />
                )}
                renderOption={(option) => (
                    <div className={cls.countryOption}>
                        {option?.flagPath && (
                            <img
                                src={option.flagPath}
                                alt="country-flag-icon"
                                width={25}
                                height={18}
                            />
                        )}
                        <span>{option.name}</span>
                    </div>
                )}
            />
        </div>
    );
};
